"use client";

import type {
  GeneratedMutation,
  GeneratedQuery,
  GraphQLResult,
} from "@aws-amplify/api-graphql";
import { getAmplifyGraphQLClient } from "./clients";

export async function fetchGraphQLClient<Variables, Response>(
  query:
    | GeneratedQuery<Variables, Response>
    | GeneratedMutation<Variables, Response>,
  variables: Variables
): Promise<GraphQLResult<Response>> {
  const client = await getAmplifyGraphQLClient();

  return client.graphql({
    query,
    variables,
  }) as Promise<GraphQLResult<Response>>;
}

export async function fetchGraphQLClientWithApiKey<Variables, Response>(
  query:
    | GeneratedQuery<Variables, Response>
    | GeneratedMutation<Variables, Response>,
  variables: Variables
): Promise<GraphQLResult<Response>> {
  const client = await getAmplifyGraphQLClient();

  return client.graphql({
    query,
    variables,
    authMode: "apiKey",
    authToken: process.env.NEXT_PUBLIC_APPSYNC_API_KEY,
  }) as Promise<GraphQLResult<Response>>;
}

interface RecursiveFetchGraphQLClientOptions<R> {
  token?: string;
  getNextToken?: (response: GraphQLResult<R>) => string | undefined | null;
}

export async function recursiveFetchGraphQLClient<Variables, Response>(
  query: GeneratedQuery<Variables, Response>,
  variables: Variables,
  options: RecursiveFetchGraphQLClientOptions<Response>
): Promise<GraphQLResult<Response>[]> {
  const client = await getAmplifyGraphQLClient();

  const recursiveCall = async (
    vars: Variables,
    token?: string | null,
    level = 0
  ): Promise<GraphQLResult<Response>[]> => {
    // Base Case
    if (level !== 0 && !token) {
      return [];
    }

    // Recursive Case
    const response = (await client.graphql({
      query,
      variables: {
        ...vars,
        nextToken: token,
      },
    })) as GraphQLResult<Response>;

    const nextToken = options.getNextToken?.(response);
    const nextResponse = await recursiveCall(vars, nextToken, level + 1);

    return [response].concat(nextResponse);
  };

  return recursiveCall(variables);
}
